import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Container, Row, Col, Card, Alert, Spinner, Button, Form } from 'react-bootstrap';
import { Link, useParams, withRouter } from "react-router-dom";
import { useSignIn } from 'react-auth-kit';
import PublicNavigation from '../components/Navigation/PublicNavigation';

const VerifyEmail = ({ history }) => {
    const { token } = useParams();
    const signIn = useSignIn();
    const apiUrl = process.env.REACT_APP_URL;

    const [status, setStatus] = useState('verifying'); // verifying, success, error, expired
    const [message, setMessage] = useState('');
    const [email, setEmail] = useState('');
    const [resendEmail, setResendEmail] = useState('');
    const [resendLoading, setResendLoading] = useState(false);
    const [resendMessage, setResendMessage] = useState('');

    useEffect(() => {
        if (token) {
            verifyEmail();
        }
    }, [token]);

    const verifyEmail = async () => {
        try {
            const response = await axios.get(`${apiUrl}/companies/verify-email/${token}`);
            const data = response.data;

            if (data.verified && data.tokens) {
                // Auto-login the user
                signIn({
                    token: data.tokens.access_token,
                    expiresIn: 604800,
                    tokenType: "Bearer",
                    authState: {
                        email: data.user.email,
                        company: data.company,
                        user: data.user
                    },
                    refreshToken: data.tokens.refresh_token
                });

                setStatus('success');
                setMessage(data.message || 'Tu correo ha sido verificado exitosamente.');

                // Redirect to dashboard after 3 seconds
                setTimeout(() => {
                    history.push('/flotilla');
                }, 3000);
            }
        } catch (error) {
            console.error('Verification error:', error);

            if (error.response && error.response.data) {
                const errorData = error.response.data;

                if (errorData.expired) {
                    setStatus('expired');
                    setEmail(errorData.email || '');
                    setResendEmail(errorData.email || '');
                } else {
                    setStatus('error');
                }
                setMessage(errorData.error || 'Error al verificar el correo.');
            } else {
                setStatus('error');
                setMessage('Error de conexión. Por favor, inténtalo más tarde.');
            }
        }
    };

    const handleResendVerification = async (e) => {
        e.preventDefault();
        setResendLoading(true);
        setResendMessage('');

        try {
            const response = await axios.post(`${apiUrl}/companies/resend-verification`, {
                email: resendEmail
            });

            if (response.data.email_sent || response.data.message) {
                setResendMessage(response.data.message || 'Correo de verificación reenviado exitosamente.');
            }
        } catch (error) {
            console.error('Resend error:', error);
            setResendMessage(error.response?.data?.error || 'Error al reenviar el correo.');
        } finally {
            setResendLoading(false);
        }
    };

    const renderContent = () => {
        switch (status) {
            case 'verifying':
                return (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
                        <h3 className="mt-4">Verificando tu correo...</h3>
                        <p className="text-muted">Por favor espera un momento.</p>
                    </div>
                );

            case 'success':
                return (
                    <div className="text-center py-4">
                        <div className="mb-4">
                            <div style={{
                                width: '80px',
                                height: '80px',
                                borderRadius: '50%',
                                backgroundColor: '#28a745',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <span style={{ fontSize: '40px', color: 'white' }}>✓</span>
                            </div>
                        </div>
                        <h3 className="text-success">¡Correo Verificado!</h3>
                        <p className="text-muted mb-4">{message}</p>
                        <Alert variant="info">
                            Serás redirigido automáticamente en unos segundos...
                        </Alert>
                        <Button variant="primary" onClick={() => history.push('/flotilla')}>
                            Ir al Dashboard
                        </Button>
                    </div>
                );

            case 'expired':
                return (
                    <div className="text-center py-4">
                        <div className="mb-4">
                            <div style={{
                                width: '80px',
                                height: '80px',
                                borderRadius: '50%',
                                backgroundColor: '#ffc107',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <span style={{ fontSize: '40px', color: 'white' }}>⏰</span>
                            </div>
                        </div>
                        <h3 className="text-warning">Enlace Expirado</h3>
                        <p className="text-muted mb-4">{message}</p>

                        <Card className="mt-4">
                            <Card.Body>
                                <h5>Reenviar correo de verificación</h5>
                                <Form onSubmit={handleResendVerification}>
                                    <Form.Group className="mb-3">
                                        <Form.Control
                                            type="email"
                                            placeholder="Tu correo electrónico"
                                            value={resendEmail}
                                            onChange={(e) => setResendEmail(e.target.value)}
                                            required
                                        />
                                    </Form.Group>
                                    <Button
                                        variant="primary"
                                        type="submit"
                                        disabled={resendLoading}
                                        className="w-100"
                                    >
                                        {resendLoading ? 'Enviando...' : 'Reenviar Correo'}
                                    </Button>
                                </Form>
                                {resendMessage && (
                                    <Alert variant="info" className="mt-3 mb-0">
                                        {resendMessage}
                                    </Alert>
                                )}
                            </Card.Body>
                        </Card>
                    </div>
                );

            case 'error':
            default:
                return (
                    <div className="text-center py-4">
                        <div className="mb-4">
                            <div style={{
                                width: '80px',
                                height: '80px',
                                borderRadius: '50%',
                                backgroundColor: '#dc3545',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <span style={{ fontSize: '40px', color: 'white' }}>✗</span>
                            </div>
                        </div>
                        <h3 className="text-danger">Error de Verificación</h3>
                        <p className="text-muted mb-4">{message}</p>

                        <div className="d-flex justify-content-center gap-3">
                            <Link to="/login">
                                <Button variant="outline-primary">Iniciar Sesión</Button>
                            </Link>
                            <Link to="/signup">
                                <Button variant="primary">Registrarse</Button>
                            </Link>
                        </div>
                    </div>
                );
        }
    };

    return (
        <div style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
            <PublicNavigation />

            <div style={{ paddingTop: '120px', paddingBottom: '50px' }}>
                <Container>
                    <Row className="justify-content-center">
                        <Col md={6} lg={5}>
                            <Card className="shadow-sm">
                                <Card.Body className="p-4">
                                    <h2 className="text-center mb-4">Verificación de Correo</h2>
                                    {renderContent()}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </div>
    );
};

export default withRouter(VerifyEmail);
